import { useState, useRef, useCallback } from 'react';
import { useReactFlow, useViewport, useStore } from '@xyflow/react';
import { shallow } from 'zustand/shallow';
import { Flex, Tooltip, Button, IconButton, Icon, Dropdown, icon } from '@nula/core-ui';
import styles from './canvas-zoom-controls.module.css';
import { canvasShortcuts } from '@/features/canvas/utils/canvas-shortcuts';

// Zoom presets surfaced in the percentage-button dropdown. 50/100/200
// — same logarithmic ratio between steps. 100 keeps its keyboard
// shortcut (⌘1); the others are click-only entries.
const ZOOM_PRESETS = [0.5, 1, 2] as const;

const PADDING = 20;
const VISIBLE_TYPES = new Set(['sticky', 'shape', 'text', 'freedraw', 'frame']);

function getNodeColor(type: string | undefined, data: Record<string, unknown> | undefined): string {
  // Mirror what the user sees: read the user-facing color field from node
  // data and fall back to a type-appropriate token if it's missing/none.
  // White/transparent fills are treated as "no visible fill" so they don't
  // disappear into the minimap's white panel background — fall through to
  // the stroke or a token instead.
  const d = data ?? {};
  const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);
  const isVisibleFill = (v: string | undefined): v is string =>
    !!v && v !== 'none' && v.toLowerCase() !== '#ffffff' && v.toLowerCase() !== '#fff' && v !== 'transparent';
  switch (type) {
    case 'sticky':
      return str(d.fill) ?? 'var(--color-yellow-200, #fde68a)';
    case 'shape': {
      const fill = str(d.fill);
      if (isVisibleFill(fill)) return fill;
      return str(d.stroke) ?? 'var(--outline-variant)';
    }
    case 'text':
      return str(d.color) ?? 'var(--on-surface-variant)';
    case 'freedraw':
      return str(d.strokeColor) ?? 'var(--on-surface-variant)';
    case 'frame': {
      // Frame uses `fill` (same field as sticky/shape), not `background`.
      const fill = str(d.fill);
      if (isVisibleFill(fill)) return fill;
      return 'color-mix(in srgb, var(--outline-variant) 30%, transparent)';
    }
    default:
      return 'var(--surface-active)';
  }
}

const MAP_W = 200;
const MAP_H = 140;

export interface CanvasZoomControlsProps {
  /** Called when the user clicks the Frames button. When undefined,
   *  the button doesn't render — keeps the engine standalone-mountable
   *  (CanvasViewer, tests) without forcing them to wire a panel. */
  onOpenFramesPanel?: () => void;
  /** Whether the frames panel is currently open. Drives the toggle's
   *  pressed state. Only consulted when onOpenFramesPanel is set. */
  framesPanelOpen?: boolean;
}

export function CanvasZoomControls({
  onOpenFramesPanel,
  framesPanelOpen = false,
}: CanvasZoomControlsProps = {}) {
  const { zoomIn, zoomOut, fitView, setViewport, getViewport } = useReactFlow();
  const { zoom, x: vpX, y: vpY } = useViewport();
  const { canvasWidth, canvasHeight, nodeLookup, rfNodes } = useStore(
    s => ({ canvasWidth: s.width, canvasHeight: s.height, nodeLookup: s.nodeLookup, rfNodes: s.nodes }),
    shallow,
  );
  const svgRef = useRef<SVGSVGElement>(null);
  const [mapOpen, setMapOpen] = useState(false);

  const zoomPercent = Math.round(zoom * 100);

  const setZoomTo = useCallback((nextZoom: number) => {
    const { x, y } = getViewport();
    setViewport({ x, y, zoom: nextZoom }, { duration: 200 });
  }, [getViewport, setViewport]);

  const handleFitView = useCallback(() => {
    fitView({ duration: 300, padding: 0.1 });
  }, [fitView]);

  // Minimap geometry
  // `nodes` (plain array) changes reference on every update so shallow catches
  // it. We look each node up in `nodeLookup` to get internals.positionAbsolute
  // (canvas-absolute coords). n.position from useNodes() is frame-local for
  // child nodes and would cluster them near origin. This mirrors how RF's own
  // MiniMap works: selectorNodeIds uses s.nodes for reactivity, then reads
  // internals per-node from nodeLookup.
  // Pair each public node with its internal counterpart. The public node has
  // the declared width/height from the Yjs snapshot (reliable even before RF
  // has measured the DOM); the internal node has positionAbsolute and userNode.
  // We need both: dimensions from the public node, position from the internal.
  const visibleNodePairs = (rfNodes ?? [])
    .map((pub) => ({ pub, internal: nodeLookup.get(pub.id) }))
    .filter((p): p is { pub: NonNullable<typeof p.pub>; internal: NonNullable<typeof p.internal> } =>
      !!p.internal && !p.internal.hidden && VISIBLE_TYPES.has(p.internal.type ?? ''));
  const hasNodes = visibleNodePairs.length > 0;
  // getNodeDimensions pattern: measured (post-DOM) → declared (pre-DOM) → 0
  const nodeDims = (pub: typeof rfNodes[0], internal: ReturnType<typeof nodeLookup.get>) => ({
    w: internal?.measured?.width ?? pub.width ?? 160,
    h: internal?.measured?.height ?? pub.height ?? 120,
  });
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const { pub, internal: n } of visibleNodePairs) {
    const nx = n.internals.positionAbsolute.x, ny = n.internals.positionAbsolute.y;
    const { w: nw, h: nh } = nodeDims(pub, n);
    if (nx < minX) minX = nx;
    if (ny < minY) minY = ny;
    if (nx + nw > maxX) maxX = nx + nw;
    if (ny + nh > maxY) maxY = ny + nh;
  }
  if (!hasNodes) { minX = 0; minY = 0; maxX = 400; maxY = 300; }
  // visibleNodes alias for the SVG render loop below
  const visibleNodes = visibleNodePairs;
  minX -= PADDING; minY -= PADDING; maxX += PADDING; maxY += PADDING;

  const contentW = maxX - minX;
  const contentH = maxY - minY;
  const scale = Math.min(MAP_W / contentW, MAP_H / contentH);
  const offsetX = (MAP_W - contentW * scale) / 2;
  const offsetY = (MAP_H - contentH * scale) / 2;

  const toMini = (cx: number, cy: number) => ({
    x: (cx - minX) * scale + offsetX,
    y: (cy - minY) * scale + offsetY,
  });

  const vpCanvasX = -vpX / zoom;
  const vpCanvasY = -vpY / zoom;
  const vpMini = toMini(vpCanvasX, vpCanvasY);
  const vpMiniW = (canvasWidth  / zoom) * scale;
  const vpMiniH = (canvasHeight / zoom) * scale;

  const handleMapClick = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    // SVG uses a viewBox of MAP_W×MAP_H but may be rendered at a different
    // pixel size (width="100%"). Convert rendered px → viewBox coords first.
    const svgScale = MAP_W / rect.width;
    const px = (e.clientX - rect.left) * svgScale;
    const py = (e.clientY - rect.top)  * svgScale;
    const canvasX = (px - offsetX) / scale + minX;
    const canvasY = (py - offsetY) / scale + minY;
    setViewport({
      x: -(canvasX * zoom) + canvasWidth  / 2,
      y: -(canvasY * zoom) + canvasHeight / 2,
      zoom,
    }, { duration: 200 });
  }, [offsetX, offsetY, scale, minX, minY, zoom, canvasWidth, canvasHeight, setViewport]);

  // Zoom-percentage dropdown. Two groups separated by a divider:
  //  1. View presets — Zoom to 50/100/200% + Fit to screen.
  //  2. Toggles — Show minimap (state-aware checkmark).
  // Built from a registry-shaped array; surfaces opt-in. canvasShortcuts.*
  // labels stay empty on touch-only devices via shortcutLabel(), so the
  // `extra` field is omitted when there's no shortcut to show.
  const zoomMenuItems = [
    ...ZOOM_PRESETS.map((preset) => {
      const pct = Math.round(preset * 100);
      const isHundred = preset === 1;
      const shortcut = isHundred ? canvasShortcuts.zoom100() : '';
      return {
        key: `zoom-${pct}`,
        label: `Zoom to ${pct}%`,
        ...(shortcut ? { extra: shortcut } : {}),
        onClick: () => setZoomTo(preset),
      };
    }),
    {
      key: 'fit',
      label: 'Fit to screen',
      ...(canvasShortcuts.fitView() ? { extra: canvasShortcuts.fitView() } : {}),
      onClick: handleFitView,
    },
    { key: 'div', label: '', type: 'divider' as const },
    {
      key: 'minimap',
      label: 'Show minimap',
      selected: mapOpen,
      onClick: () => setMapOpen((o) => !o),
    },
  ];

  return (
    <div className={styles.root}>
      {mapOpen && (
        <div className={styles.mapPanel}>
          <svg
            ref={svgRef}
            width="100%"
            height={MAP_H}
            viewBox={`0 0 ${MAP_W} ${MAP_H}`}
            preserveAspectRatio="xMidYMid meet"
            data-testid="minimap-canvas"
            className={styles.mapSvg}
            onClick={handleMapClick}
          >
            {visibleNodes.map(({ pub, internal: n }) => {
              const { x, y } = toMini(n.internals.positionAbsolute.x, n.internals.positionAbsolute.y);
              const { w, h } = nodeDims(pub, n);
              return (
                <rect
                  key={n.id}
                  data-testid="minimap-node"
                  x={x} y={y}
                  width={Math.max(2, w * scale)}
                  height={Math.max(2, h * scale)}
                  rx={2}
                  fill={getNodeColor(n.type, n.internals.userNode.data as Record<string, unknown> | undefined)}
                  opacity={0.85}
                />
              );
            })}
            <rect
              data-testid="minimap-viewport"
              x={vpMini.x} y={vpMini.y}
              width={Math.max(4, vpMiniW)}
              height={Math.max(4, vpMiniH)}
              fill="none"
              stroke="var(--primary)"
              strokeWidth={1.5}
              rx={2}
              opacity={0.7}
            />
          </svg>
        </div>
      )}

      <Flex align="center" gap={0} className={styles.pill}>
        {/* Frames-panel toggle. Sits where the now-removed minimap
            button used to be (leftmost in the pill). Only renders
            when the host wires onOpenFramesPanel; otherwise the
            standalone CanvasZoomControls (e.g. CanvasViewer) keeps
            its existing layout. */}
        {onOpenFramesPanel && (
          <>
            <Tooltip
              title={framesPanelOpen ? 'Hide frames' : 'Show frames'}
              placement="top"
            >
              <IconButton
                aria-label="Frames"
                aria-pressed={framesPanelOpen}
                className={styles.btn}
                onClick={onOpenFramesPanel}
              >
                <Icon name="view_list" size={icon.md} />
              </IconButton>
            </Tooltip>
            <div className={styles.divider} />
          </>
        )}

        <Tooltip title={['Zoom out', canvasShortcuts.zoomOut()].filter(Boolean).join(' · ')} placement="top">
          <IconButton
            aria-label="Zoom out"
            className={styles.btn}
            onClick={() => zoomOut({ duration: 200 })}
          >
            <Icon name="remove" size={icon.md} weight={300} />
          </IconButton>
        </Tooltip>

        <Dropdown
          menu={{ items: zoomMenuItems }}
          trigger={['click']}
          placement="top"
        >
          <Button
            variant="text"
            size="sm"
            aria-label="Zoom options"
            aria-haspopup="menu"
            className={styles.zoomLabel}
          >
            {zoomPercent}%
          </Button>
        </Dropdown>

        <Tooltip title={['Zoom in', canvasShortcuts.zoomIn()].filter(Boolean).join(' · ')} placement="top">
          <IconButton
            aria-label="Zoom in"
            className={styles.btn}
            onClick={() => zoomIn({ duration: 200 })}
          >
            <Icon name="add" size={icon.md} weight={300} />
          </IconButton>
        </Tooltip>
      </Flex>
    </div>
  );
}
